import { lazy, Suspense, useEffect, useState } from "react";

const ChatWidget = lazy(() =>
  import("@/components/ChatWidget").then((m) => ({ default: m.ChatWidget }))
);

/**
 * Loads the chat widget (and its Supabase client) only after the page has
 * finished loading and is idle (3s fallback) or the visitor first interacts,
 * keeping that code out of the initial homepage bundle.
 */
export const DeferredChatWidget = () => {
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    if (shouldLoad) return;

    const load = () => setShouldLoad(true);
    const onIdle = () => {
      if (typeof window.requestIdleCallback === "function") {
        window.requestIdleCallback(load, { timeout: 3000 });
      } else {
        window.setTimeout(load, 3000);
      }
    };

    if (document.readyState === "complete") onIdle();
    else window.addEventListener("load", onIdle, { once: true });

    const events: (keyof WindowEventMap)[] = ["pointerdown", "keydown", "touchstart", "scroll"];
    events.forEach((evt) => window.addEventListener(evt, load, { once: true, passive: true }));

    return () => {
      window.removeEventListener("load", onIdle);
      events.forEach((evt) => window.removeEventListener(evt, load));
    };
  }, [shouldLoad]);

  if (!shouldLoad) return null;

  return (
    <Suspense fallback={null}>
      <ChatWidget />
    </Suspense>
  );
};

export default DeferredChatWidget;
